import { Fragment, ReactNode } from 'react'
import { match } from '@st/pattern'
import { setAdd, setRemove } from '@st/util/array-set'
import { isNotEmpty, JSONValue, toBool, toStr, toStrArray } from '@st/util/json-value'
import { Document, Flexible, Page } from '@st/document-rendering'
import {
  Box,
  Checkbox,
  Column,
  Expand,
  HiddenInput,
  Radio,
  Row,
  Select,
  Space,
  Text,
  TextInput,
  Pad,
  Table
} from './document-elements'
import {
  DEFAULT_FORMATION_FIELD_DECORATION,
  type FormationFieldChange,
  type FormationPageFields
} from './render-data'

export const FORM_BORDER_GREY = '#cdcdcc'

const PAGE_PADDING = Pad.all(40)
const HEADING_PADDING = Pad.horizontal(20)
const BLACK = '#000000'
const ORANGE = '#daa00b'
const WHITE = '#ffffff'
const TEXT_SIZE = 11
const HINT_SIZE = 8

/** Height of a single-line entry field; a row's children size their own height. */
export const FIELD_HEIGHT = 20

export type LabelPosition = 'left' | 'right'

export function PDFFormDocument({
  title,
  children
}: {
  title?: string
  children?: ReactNode
}) {
  return (
    <Document title={title}>
      {children}
    </Document>
  )
}

export function PDFFormPage({
  width,
  height,
  padding = PAGE_PADDING,
  children
}: {
  width: number
  height: number
  padding?: Pad
  children: ReactNode
}) {
  return (
    <Page width={width} height={height}>
      <PDFFormPageContent width={width} height={height} padding={padding}>
        {children}
      </PDFFormPageContent>
    </Page>
  )
}

export function PDFFormPageContent({
  width,
  height,
  padding = PAGE_PADDING,
  children
}: {
  width: number
  height: number
  padding?: Pad
  children: ReactNode
}) {
  return (
    <Box width={width} height={height} padding={padding} backgroundColor={WHITE}>
      <Column>{children}</Column>
    </Box>
  )
}

export function PageHeading({ children, hasData }: { children: string; hasData?: boolean }) {
  return (
    <Box
      width={Infinity}
      height={26}
      padding={HEADING_PADDING}
      vAlign="center"
      backgroundColor={hasData ? ORANGE : BLACK}
      role="heading"
      aria-level={1}
      aria-label={hasData ? `${children} highlighted` : children}
    >
      <Text fontSize={14} fontWeight="bold" color={WHITE}>
        {children}
      </Text>
    </Box>
  )
}

export function CommentSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <Column mainAxisSize="min">
      {title ? <SectionHeading>{title}</SectionHeading> : null}
      {children}
    </Column>
  )
}

function SectionHeading({ children }: { children: string }) {
  return (
    <Box
      width={Infinity}
      height={20}
      padding={HEADING_PADDING}
      vAlign="center"
      backgroundColor={BLACK}
      borderColor={BLACK}
      borderWidth={1}
      role="heading"
      aria-level={2}
      aria-label={children}
    >
      <Text fontSize={12} fontWeight="bold" color={WHITE}>
        {children}
      </Text>
    </Box>
  )
}

export function SectionSubheading({ children }: { children: string }) {
  return (
    <Box
      width={Infinity}
      height={20}
      padding={HEADING_PADDING}
      vAlign="center"
      borderWidth={1}
      borderColor={FORM_BORDER_GREY}
      role="heading"
      aria-level={3}
      aria-label={children}
    >
      <Text fontSize={TEXT_SIZE}>{children}</Text>
    </Box>
  )
}

/**
 * An organizer row: the bordered, indented line that holds its children in a
 * Row. Children compose their own sizing, as in a Flutter Row: Expanded
 * children share the row width, inflexible ones keep their intrinsic width.
 * The row grows past its minimum height when a child needs more, such as a
 * wrapped question.
 */
export function SectionRow({
  children,
  minHeight = 20,
  indent = 0,
  bullet
}: {
  children: ReactNode
  minHeight?: number
  indent?: number
  bullet?: string
}) {
  const bulletWidth = match(bullet)
    .with(undefined, () => 0)
    .otherwise(() => BULLET_WIDTH)
  const indentAmount = SECTION_ROW_LEFT_PADDING + indent * INDENT_WIDTH - bulletWidth

  return (
    <Box
      width={Infinity}
      minHeight={minHeight}
      padding={Pad.only({ left: indentAmount })}
      borderWidth={0.5}
      borderColor={FORM_BORDER_GREY}
    >
      <Row align="center">
        {bullet && (
          <Box width={bulletWidth} height={20} vAlign="center" padding={Pad.only({ left: 6 })}>
            <Text fontSize={11}>{bullet}</Text>
          </Box>
        )}
        {children}
      </Row>
    </Box>
  )
}

const INDENT_WIDTH = 40
const BULLET_WIDTH = 20
const SECTION_ROW_LEFT_PADDING = 20

export function FormTextInput({
  label,
  inputKey,
  multiline,
  shouldShowHint,
  mask,
  fontSize = TEXT_SIZE,
  fields,
  onFieldChange
}: {
  label?: string
  inputKey: string
  multiline?: boolean
  shouldShowHint?: boolean
  mask?: boolean
  fontSize?: number
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const field = fields[inputKey]
  const value = field?.value
  const fieldDecoration = field?.decoration ?? DEFAULT_FORMATION_FIELD_DECORATION
  const hint = fieldDecoration.hint

  const textInput = (
    <TextInput
      fontSize={fontSize}
      name={inputKey}
      aria-label={label}
      value={castJsonValueToString(value)}
      onChange={(value) => onFieldChange(inputKey, value)}
      multiline={multiline}
      mask={mask}
      {...fieldDecoration}
    />
  )

  if (multiline) return textInput

  const showLabel = isNotEmpty(label)
  const showHint = isNotEmpty(hint) && shouldShowHint
  const showSpace = showLabel || showHint

  return (
    <Row align="center">
      {showLabel ? <Text fontSize={TEXT_SIZE}>{label}</Text> : null}
      {showHint ? (
        <>
          <Space w={6} />
          <Text fontSize={HINT_SIZE} fontStyle="italic">
            {`(${hint})`}
          </Text>
        </>
      ) : null}
      {showSpace ? <Space w={12} /> : null}
      <Expand>
        <Box height={FIELD_HEIGHT}>{textInput}</Box>
      </Expand>
    </Row>
  )
}

function castJsonValueToString(value: JSONValue): string {
  switch (typeof value) {
    case 'string':
      return value
    case 'number':
      return value.toString()
    default:
      return ''
  }
}

export function FormNameInput({
  userInputKey,
  fields,
  onFieldChange
}: {
  userInputKey: string
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  return (
    <Row align="center">
      <Label>Name</Label>
      <Space w={4} />
      <Expand>
        <WiredTextInput
          userInputKey={`${userInputKey}.first_name`}
          ariaLabel="First name"
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
      <Space w={1} />
      <Expand>
        <WiredTextInput
          userInputKey={`${userInputKey}.last_name`}
          ariaLabel="Last name"
          fields={fields}
          onFieldChange={onFieldChange}
        />
      </Expand>
    </Row>
  )
}

function WiredTextInput({
  userInputKey,
  ariaLabel,
  fields,
  onFieldChange
}: {
  userInputKey: string
  ariaLabel: string
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const field = fields[userInputKey]
  const value = field?.value
  const fieldDecoration = field?.decoration ?? DEFAULT_FORMATION_FIELD_DECORATION

  return (
    <Box height={FIELD_HEIGHT}>
      <TextInput
        fontSize={TEXT_SIZE}
        name={userInputKey}
        aria-label={ariaLabel}
        value={toStr(value)}
        onChange={(value) => onFieldChange(userInputKey, value)}
        {...fieldDecoration}
      />
    </Box>
  )
}

export type RadioOption = {
  label: string
  value: string | boolean | number
}

export function RadioSelectInput({
  label,
  labelPosition = 'left',
  userInputKey,
  options,
  fields,
  onFieldChange
}: {
  label: string
  labelPosition?: LabelPosition
  userInputKey: string
  options: RadioOption[]
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const value = fields[userInputKey]?.value
  const answers = (
    <Row align="center" mainAxisSize="min">
      {options.map((opt, idx) => (
        <Fragment key={idx}>
          <Radio
            name={userInputKey}
            value={opt.value}
            aria-label={opt.label}
            selected={opt.value == value}
            onChange={(v) => {
              if (!v) return
              onFieldChange(userInputKey, opt.value)
            }}
            {...getRadioCheckboxStyle(opt.value == value)}
          />
          <Space w={6} />
          <Text fontSize={TEXT_SIZE}>{opt.label}</Text>
          <Space w={6} />
        </Fragment>
      ))}
    </Row>
  )

  return match(labelPosition)
    .with('left', () => (
      <Row align="center">
        <QuestionText>{label}</QuestionText>
        <Space w={12} h={20} />
        {answers}
      </Row>
    ))
    .with('right', () => (
      <Row align="center">
        {answers}
        <Space w={12} h={20} />
        <QuestionText>{label}</QuestionText>
      </Row>
    ))
    .exhaustive()
}

/** A question's text takes the row width left by its answers and wraps within it. */
function QuestionText({ children }: { children: string }) {
  return (
    <Expand>
      <Box padding={Pad.only({ top: 2, bottom: 2 })}>
        <Text fontSize={TEXT_SIZE}>{children}</Text>
      </Box>
    </Expand>
  )
}

export type MultiSelectOption = {
  label: string
  value: string
}

export function MultiSelectInput({
  label,
  userInputKey,
  options,
  fields,
  onFieldChange
}: {
  label?: string
  userInputKey: string
  options: MultiSelectOption[]
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const selectedValues = toStrArray(fields[userInputKey]?.value)

  return (
    <Row align="center">
      <HiddenInput name={userInputKey} value="" />
      {isNotEmpty(label) && (
        <>
          <QuestionText>{label}</QuestionText>
          <Space w={12} h={20} />
        </>
      )}
      <Row align="center" mainAxisSize="min">
        {options.map((opt, idx) => {
          const name = `${userInputKey}.${opt.value}`
          return (
            <Fragment key={idx}>
              <Checkbox
                name={name}
                aria-label={opt.label}
                value={selectedValues.includes(opt.value)}
                onChange={(v) => {
                  onFieldChange(
                    userInputKey,
                    v ? setAdd(selectedValues, opt.value) : setRemove(selectedValues, opt.value)
                  )
                }}
              />
              <Space w={6} />
              <Text fontSize={TEXT_SIZE}>{opt.label}</Text>
              <Space w={6} />
            </Fragment>
          )
        })}
      </Row>
    </Row>
  )
}

export function YesNoRadio({
  label,
  labelPosition,
  userInputKey,
  fields,
  onFieldChange
}: {
  label: string
  labelPosition?: 'left' | 'right'
  userInputKey: string
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  return (
    <RadioSelectInput
      label={label}
      labelPosition={labelPosition}
      userInputKey={userInputKey}
      fields={fields}
      onFieldChange={onFieldChange}
      options={[
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ]}
    />
  )
}

export function CheckboxInput({
  label,
  userInputKey,
  fields,
  onFieldChange
}: {
  label: string
  userInputKey: string
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const value = fields[userInputKey]?.value

  return (
    <Row align="center" mainAxisSize="min">
      <Checkbox
        name={userInputKey}
        aria-label={label}
        value={toBool(value)}
        onChange={(value) => onFieldChange(userInputKey, value)}
        {...getRadioCheckboxStyle(toBool(value))}
      />
      <Space w={8} h={20} />
      <Flexible>
        <Box padding={Pad.only({ top: 2, bottom: 2 })}>
          <Text fontSize={TEXT_SIZE}>{label}</Text>
        </Box>
      </Flexible>
    </Row>
  )
}

export function Label({
  children,
  fontSize = TEXT_SIZE,
  bold
}: {
  children: string
  fontSize?: number
  bold?: boolean
}) {
  return (
    <Text fontSize={fontSize} fontWeight={bold ? 'bold' : 'normal'}>
      {children}
    </Text>
  )
}

export function FormTable({
  colCount,
  rowCount,
  colFlex = () => 1,
  colHeader,
  rowHeader,
  children
}: {
  rowCount: number
  colCount: number
  colFlex?: (colIndex: number) => number
  children: (rowIndex: number, colIndex: number) => ReactNode
  rowHeader?: (rowIndex: number) => string
  colHeader?: (colIndex: number) => string
}) {
  const hasExtraCol = !!rowHeader
  const hasExtraRow = !!colHeader
  const totalRowCount = rowCount + (hasExtraRow ? 1 : 0)
  const totalColCount = colCount + (hasExtraCol ? 1 : 0)

  return (
    <Table
      colCount={totalColCount}
      rowCount={totalRowCount}
      rowHeight={() => 20}
      colFlex={(colIndex) => {
        if (hasExtraCol) colIndex -= 1
        if (colIndex == -1) return 1
        return colFlex(colIndex)
      }}
    >
      {(rowIndex, colIndex) => {
        if (hasExtraRow) rowIndex -= 1
        if (hasExtraCol) colIndex -= 1
        if (rowIndex == -1 && colIndex == -1) return <Box height={0} width={0} />
        if (rowIndex == -1) return <TableHeader hAlign="end">{colHeader!(colIndex)}</TableHeader>
        if (colIndex == -1) return <TableHeader hAlign="end">{rowHeader!(rowIndex)}</TableHeader>

        return (
          <Box width={Infinity} height={20} padding={Pad.only({ left: 2 })}>
            {children(rowIndex, colIndex)}
          </Box>
        )
      }}
    </Table>
  )
}

function TableHeader({
  children,
  hAlign
}: {
  children: string
  hAlign: 'start' | 'center' | 'end'
}) {
  return (
    <Box width={Infinity} height={20} vAlign="center" hAlign={hAlign}>
      <Label fontSize={8} bold={true}>
        {children}
      </Label>
    </Box>
  )
}

function getRadioCheckboxStyle(selected: boolean) {
  return selected ? { backgroundColor: '#daa00b' } : { backgroundColor: '#ffffff' }
}
